import React, {
  useCallback, useRef, useState,
} from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { RichTextEditor, OutlinedIcons, ZSpinner } from '@marageti/z4-lib';
import { TicketComment } from '@marageti/z4-sdk/lib/tickets';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useSnackbar } from 'notistack';
import TicketCommentItem from './ticket-comment-item';
import TicketFileUploadModal from '../../components/modals/ticket-file-upload-modal';
import TicketCommentEditModal from '../../components/modals/ticket-comment-edit-modal';

const { OutlinedUploadIcon } = OutlinedIcons;

const ACCEPTED_FILE_TYPES = 'text/plain, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, image/png, image/jpeg';

type TicketCommentsPanelProps = {
  comments: TicketComment[];
  isLoading: boolean;
  hasMore: boolean;
  currentUserId?: string;
  onLoadMore: () => void;
  onCreateComment: (text: string) => Promise<void>;
  onCreateFileComment: (file: File, title: string) => Promise<any>;
  onUpdateComment: (commentId: string, text: string) => Promise<void>;
  onDownloadFile: (comment: TicketComment) => void;
};

const TicketCommentsPanel = ({
  comments,
  isLoading,
  hasMore,
  currentUserId,
  onLoadMore,
  onCreateComment,
  onCreateFileComment,
  onUpdateComment,
  onDownloadFile,
}: TicketCommentsPanelProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const editorStateRef = useRef<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [commentText, setCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploadModalOpen, setUploadModalOpen] = useState(false);
  const [editingComment, setEditingComment] = useState<TicketComment | null>(null);

  const handleSubmitComment = useCallback(async () => {
    if (!commentText.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onCreateComment(commentText);
      setCommentText('');
      editorStateRef.current?.clear();
    } catch {
      enqueueSnackbar('Failed to add comment.', { variant: 'default' });
    } finally {
      setIsSubmitting(false);
    }
  }, [commentText, isSubmitting, onCreateComment, enqueueSnackbar]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadModalOpen(true);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadSubmit = useCallback(async (title: string) => {
    if (!selectedFile) return;
    try {
      await onCreateFileComment(selectedFile, title);
      enqueueSnackbar('File uploaded.', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to upload file.', { variant: 'default' });
    } finally {
      setSelectedFile(null);
      setUploadModalOpen(false);
    }
  }, [selectedFile, onCreateFileComment, enqueueSnackbar]);

  const handleCloseUploadModal = () => {
    setSelectedFile(null);
    setUploadModalOpen(false);
  };

  const [sentryRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage: hasMore,
    onLoadMore,
    rootMargin: '0px 0px -400px 0px',
  });

  const handleEdit = useCallback((comment: TicketComment) => {
    setEditingComment(comment);
  }, []);

  const handleSaveEdit = useCallback(async (text: string) => {
    if (!editingComment) return;
    try {
      await onUpdateComment(editingComment.id, text);
      enqueueSnackbar('Comment updated.', { variant: 'success' });
      setEditingComment(null);
    } catch {
      enqueueSnackbar('Failed to update comment.', { variant: 'default' });
    }
  }, [editingComment, onUpdateComment, enqueueSnackbar]);

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', height: '100%', width: '100%',
    }}
    >
      <Stack
        spacing={0.5}
        sx={{
          flex: 1,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          display: 'flex',
          flexDirection: 'column-reverse',
          px: 5,
        }}
      >
        {isLoading && comments.length === 0 && (
          <Box display="flex" justifyContent="center" py={4}>
            <ZSpinner />
          </Box>
        )}

        {!isLoading && comments.length === 0 && (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              No comments yet
            </Typography>
          </Box>
        )}

        {comments.map((comment) => (
          <TicketCommentItem
            key={comment.id}
            comment={comment}
            currentUserId={currentUserId}
            onEdit={handleEdit}
            onDownloadFile={onDownloadFile}
          />
        ))}

        {hasMore && comments.length > 0 && (
          <Box display="flex" alignItems="center" ref={sentryRef}>
            <ZSpinner />
          </Box>
        )}
      </Stack>

      <Box sx={{ pb: 3, pt: 1, px: 5 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_FILE_TYPES}
          style={{ display: 'none' }}
          onChange={handleFileSelected}
        />
        <Box sx={{ position: 'relative' }}>
          <RichTextEditor
            editorName="ticket-comment-editor"
            onChange={setCommentText}
            placeholder="Add a Comment"
            editorStateRef={editorStateRef}
            useLinks
            useLists
            sx={{
              '& > .MuiStack-root': { pl: '40px' },
              minHeight: '120px',
            }}
            endAdornment={(
              <Button
                variant="contained"
                size="small"
                onClick={handleSubmitComment}
                disabled={!commentText.trim() || isSubmitting}
                sx={{ minWidth: 'auto', px: 2 }}
              >
                Submit
              </Button>
            )}
          />
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              position: 'absolute',
              bottom: 14,
              left: 10,
            }}
          >
            <IconButton
              onClick={handleUploadClick}
              sx={{
                px: 0.9, pt: 0.8, pb: 0.8, borderRadius: '8px',
              }}
            >
              <OutlinedUploadIcon sx={{ width: 20, height: 20 }} />
            </IconButton>
            <Divider orientation="vertical" flexItem sx={{ mr: 1 }} />
          </Stack>
        </Box>
      </Box>

      <TicketFileUploadModal
        open={isUploadModalOpen}
        file={selectedFile}
        onClose={handleCloseUploadModal}
        onSubmit={handleUploadSubmit}
      />

      <TicketCommentEditModal
        open={!!editingComment}
        onClose={() => setEditingComment(null)}
        onSave={handleSaveEdit}
        defaultText={editingComment?.text || ''}
      />
    </Box>
  );
};

export default TicketCommentsPanel;
